import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import VerificationBadges from '@/components/VerificationBadges';
import ChangePasswordCard from '@/components/ChangePasswordCard';
import { MarketplaceDisclaimer } from '@/components/MarketplaceDisclaimer';
import { PROFILE_COPY, bi } from '@/data/legal';
import { US_STATES } from '@/data/constants';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

/**
 * Post-registration profile settings. Licensing and insurance live HERE and are
 * always optional — registration is never blocked by licensing status.
 */
const ProfileSettings: React.FC = () => {
  const { lang, path, t } = useI18n();
  const { profile, updateProfile, loading } = useAuth();
  const [f, setF] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const c = (k: keyof typeof PROFILE_COPY) => bi(PROFILE_COPY[k] as { en: string; es: string }, lang);

  useEffect(() => {
    if (!profile) return;
    setF({
      company_name: profile.company_name || '',
      contact_name: profile.contact_name || '',
      phone: profile.phone || '',
      city: profile.city || '',
      state: profile.state || '',
      years_in_business: profile.years_in_business != null ? String(profile.years_in_business) : '',
      employees: profile.employees != null ? String(profile.employees) : '',
      service_areas: (profile.service_areas || []).join(', '),
      license_number: profile.license_number || '',
      license_state: profile.license_state || '',
      insurance_carrier: profile.insurance_carrier || '',
      insurance_policy: profile.insurance_policy || '',
    });
  }, [profile]);

  const set = (k: string, v: string) => {
    setF((p) => ({ ...p, [k]: v }));
    setSaved(false);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await updateProfile({
      company_name: f.company_name || null,
      contact_name: f.contact_name || null,
      phone: f.phone || null,
      city: f.city || null,
      state: f.state || null,
      years_in_business: f.years_in_business ? Number(f.years_in_business) : null,
      employees: f.employees ? Number(f.employees) : null,
      service_areas: (f.service_areas || '').split(',').map((s) => s.trim()).filter(Boolean),
      license_number: f.license_number || null,
      license_state: f.license_state || null,
      insurance_carrier: f.insurance_carrier || null,
      insurance_policy: f.insurance_policy || null,
    });
    setBusy(false);
    setSaved(true);
  };

  if (loading) return <div className="px-4 py-16 text-center text-slate-500">{t('common.loading')}</div>;
  if (!profile)
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <p className="text-slate-600">{lang === 'es' ? 'Inicie sesión para ver su perfil.' : 'Sign in to view your profile.'}</p>
        <Link to={path('/login')} className="mt-4 inline-block rounded-xl bg-orange-500 px-6 py-3 font-bold text-white">
          {t('nav.login')}
        </Link>
      </div>
    );

  const isCompany = profile.user_type !== 'homeowner';

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <Seo title={c('title')} description={c('subtitle')} noindex />
      <Link to={path('/dashboard')} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </Link>

      <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">{c('title')}</h1>
      <p className="mt-2 text-sm text-slate-500">{c('subtitle')}</p>

      <form onSubmit={save} className="mt-8 space-y-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-bold text-slate-900">{c('business')}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {isCompany && (
              <>
                <div className="sm:col-span-2"><label className={labelCls}>{t('auth.companyName')}</label><input className={inputCls} value={f.company_name || ''} onChange={(e) => set('company_name', e.target.value)} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>{profile.user_type === 'gc' ? t('auth.contactName') : t('auth.ownerName')}</label><input className={inputCls} value={f.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} /></div>
              </>
            )}
            <div><label className={labelCls}>{t('common.phone')}</label><input type="tel" className={inputCls} value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} /></div>
            <div><label className={labelCls}>{t('common.city')}</label><input className={inputCls} value={f.city || ''} onChange={(e) => set('city', e.target.value)} /></div>
            <div>
              <label className={labelCls}>{t('common.state')}</label>
              <select className={inputCls} value={f.state || ''} onChange={(e) => set('state', e.target.value)}>
                <option value="">—</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {isCompany && (
              <>
                <div><label className={labelCls}>{t('auth.years')}</label><input type="number" min="0" className={inputCls} value={f.years_in_business || ''} onChange={(e) => set('years_in_business', e.target.value)} /></div>
                <div><label className={labelCls}>{t('auth.employees')}</label><input type="number" min="0" className={inputCls} value={f.employees || ''} onChange={(e) => set('employees', e.target.value)} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>{t('auth.serviceAreas')}</label><input className={inputCls} value={f.service_areas || ''} onChange={(e) => set('service_areas', e.target.value)} /></div>
              </>
            )}
          </div>
        </section>

        {isCompany && (
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="text-lg font-bold text-slate-900">{c('credentials')}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">{c('optionalNote')}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><label className={labelCls}>{c('licenseNumber')}</label><input className={inputCls} value={f.license_number || ''} onChange={(e) => set('license_number', e.target.value)} placeholder={lang === 'es' ? 'Opcional' : 'Optional'} /></div>
              <div>
                <label className={labelCls}>{c('licenseState')}</label>
                <select className={inputCls} value={f.license_state || ''} onChange={(e) => set('license_state', e.target.value)}>
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>{c('insuranceCarrier')}</label><input className={inputCls} value={f.insurance_carrier || ''} onChange={(e) => set('insurance_carrier', e.target.value)} placeholder={lang === 'es' ? 'Opcional' : 'Optional'} /></div>
              <div><label className={labelCls}>{c('insurancePolicy')}</label><input className={inputCls} value={f.insurance_policy || ''} onChange={(e) => set('insurance_policy', e.target.value)} placeholder={lang === 'es' ? 'Opcional' : 'Optional'} /></div>
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> {c('verification')}
          </h2>
          <div className="mt-3">
            <VerificationBadges
              licenseVerified={profile.license_verified}
              insuranceVerified={profile.insurance_verified}
              backgroundVerified={profile.background_verified}
              showUnverifiedNote
            />
          </div>
          <p className="mt-3 text-xs leading-relaxed text-slate-500">{c('verificationNote')}</p>
          {profile.terms_accepted_at && (
            <p className="mt-3 text-xs text-slate-400">
              {c('acceptedOn')} {new Date(profile.terms_accepted_at).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US')}
              {profile.terms_version ? ` · v${profile.terms_version}` : ''}
            </p>
          )}
        </section>

        <MarketplaceDisclaimer variant="short" />

        <div className="flex items-center gap-3">
          <button type="submit" disabled={busy} className="rounded-xl bg-orange-500 px-6 py-3 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-50">
            {busy ? c('saving') : c('save')}
          </button>
          {saved && <span className="text-sm font-semibold text-emerald-700">{c('saved')}</span>}
        </div>
      </form>

      {/* Password management lives outside the profile <form> so the two
          submissions never interfere with each other. */}
      <div className="mt-6">
        <ChangePasswordCard userId={profile.id} />
      </div>
    </div>
  );
};

export default ProfileSettings;
